import React from 'react';

// Menu item names on the reports.
//
// item_master carries both names -- item_name is the Gujarati one, eng_name the English one.
// Every report READS in Gujarati and SEARCHES in either language: the cell shows the Gujarati
// name with the English one hidden beside it, and the inline column filters match against both,
// so typing "Rotli" and typing "રોટલી" each find રોટલી.

// The name a cell shows. Falls back to English for an item with no Gujarati name recorded,
// so a Menu Item cell is never blank.
const itemDisplayName = (row) => (row?.item_name || row?.eng_name || '');

// What a Menu Item filter matches against -- both names, whichever one was typed.
const itemSearchText = (row) => {
    const guj = itemDisplayName(row);
    const eng = row?.eng_name ?? '';
    return (eng && eng !== guj ? `${guj} ${eng}` : guj).trim();
};

// What an exported sheet writes. A file has no hidden label to search, so both names are
// spelled out there.
const itemExportName = (row) => {
    const guj = itemDisplayName(row);
    const eng = row?.eng_name ?? '';
    return eng && eng !== guj ? `${guj} (${eng})` : guj;
};

// The Gujarati name with the English one beside it as a screen-reader-only label: read as
// Gujarati, still found when the English name is searched for.
const ItemName = ({ item }) => {
    const guj = itemDisplayName(item);
    const eng = item?.eng_name ?? '';
    return (
        <>
            {guj}
            {eng && eng !== guj ? <span className="sr-only"> {eng}</span> : null}
        </>
    );
};

// The same two names stacked: Gujarati on its own line with the English name under it.
// This is the operational screens' and master lists' rendering -- a working screen has room for
// both, and reading one name against the other is the point of it. The reports keep ItemName
// above instead: their Menu Item column is one of thirty, and a second line in every row costs
// more there than it gives.
const ItemNameStacked = ({ item }) => {
    const guj = itemDisplayName(item);
    const eng = item?.eng_name ?? '';
    // Nothing to stack when an item carries one name, or the same name twice.
    if (!eng || eng === guj) return <>{guj}</>;
    return (
        <>
            {guj}
            <br />
            <span className="text-xs text-gray-500">{eng}</span>
        </>
    );
};

// react-select option for an Item dropdown: the chip and the list read in Gujarati, while
// `search` carries both names for matchItemOption below.
const itemSelectOption = (item) => ({
    value: item.id,
    label: itemDisplayName(item),
    search: itemSearchText(item),
});

// Item dropdown matching. Every whitespace-separated word must appear, so "rotli sada" narrows
// the same way it did when the option label spelled out both names.
const matchItemOption = (option, inputValue) => {
    const haystack = String(option?.data?.search ?? option?.data?.label ?? option?.label ?? '').toLowerCase();
    return String(inputValue ?? '').toLowerCase().split(/\s+/).filter(Boolean)
        .every((token) => haystack.includes(token));
};

export { ItemName, ItemNameStacked, itemDisplayName, itemSearchText, itemExportName, itemSelectOption, matchItemOption };
